import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getDailyReports,
  addCoachFoodEntry,
  updateFoodEntry,
  markReportReviewed,
  addCoachNote,
  deleteCoachNote,
  getReportsAwaitingReview,
  getMeasurementsDue,
  sendMeasurementReminder,
} from "../../firebase";

/**
 * Daily eating reports (דוחות אכילה) + the coach's nutrition pending queue —
 * nutrition clinics only.
 *
 * The queue tags are clinic-wide, so reviewing one report refreshes the queue
 * and the patient's own list together.
 */
export const dailyReportsApi = createApi({
  reducerPath: "dailyReportsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["DailyReports", "ReviewQueue", "MeasurementsDue"],
  endpoints: (builder) => ({
    getDailyReports: builder.query({
      async queryFn({ clinicId, patientId, limitCount }) {
        try {
          const reports = await getDailyReports(clinicId, patientId, limitCount);
          return { data: reports };
        } catch (error) {
          console.error("[CK-7819] Error fetching daily reports:", {
            errorCode: "CK-7819",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
      ],
    }),

    // Coach logs a meal on the client's behalf (tagged source: "coach")
    addCoachFoodEntry: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        dateKey,
        entryData,
        therapistId,
        therapistName,
      }) {
        try {
          const entry = await addCoachFoodEntry(
            clinicId,
            patientId,
            dateKey,
            entryData,
            { therapistId, therapistName }
          );
          return { data: entry };
        } catch (error) {
          console.error("[CK-7820] Error adding a coach food entry:", {
            errorCode: "CK-7820",
            errorMessage: error.message,
            clinicId,
            patientId,
            dateKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
      ],
    }),

    updateFoodEntry: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        dateKey,
        entryId,
        changes,
        therapistId,
        therapistName,
      }) {
        try {
          const result = await updateFoodEntry(
            clinicId,
            patientId,
            dateKey,
            entryId,
            changes,
            { therapistId, therapistName }
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7821] Error updating a food entry:", {
            errorCode: "CK-7821",
            errorMessage: error.message,
            clinicId,
            patientId,
            dateKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
      ],
    }),

    // What removes a report from the pending queue
    markReportReviewed: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        dateKey,
        rating,
        therapistId,
        therapistName,
      }) {
        try {
          const result = await markReportReviewed(
            clinicId,
            patientId,
            dateKey,
            { rating },
            { therapistId, therapistName }
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7822] Error marking a report reviewed:", {
            errorCode: "CK-7822",
            errorMessage: error.message,
            clinicId,
            patientId,
            dateKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
        { type: "ReviewQueue", id: "LIST" },
      ],
    }),

    // ===== Coach notes (menus / measurements / daily reports) =====

    addCoachNote: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        target,
        docId,
        content,
        therapistId,
        therapistName,
      }) {
        try {
          const note = await addCoachNote(
            clinicId,
            patientId,
            target,
            docId,
            content,
            { therapistId, therapistName }
          );
          return { data: note };
        } catch (error) {
          console.error("[CK-7823] Error adding a coach note:", {
            errorCode: "CK-7823",
            errorMessage: error.message,
            clinicId,
            patientId,
            target,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
      ],
    }),

    deleteCoachNote: builder.mutation({
      async queryFn({ clinicId, patientId, target, docId, note }) {
        try {
          const result = await deleteCoachNote(
            clinicId,
            patientId,
            target,
            docId,
            note
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7824] Error deleting a coach note:", {
            errorCode: "CK-7824",
            errorMessage: error.message,
            clinicId,
            patientId,
            target,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "DailyReports", id: `PATIENT-${patientId}` },
      ],
    }),

    // ===== The pending queue =====

    getReportsAwaitingReview: builder.query({
      async queryFn({ clinicId, therapistId, isAdmin }) {
        try {
          const reports = await getReportsAwaitingReview(clinicId, therapistId, {
            isAdmin,
          });
          return { data: reports };
        } catch (error) {
          console.error("[CK-7825] Error fetching the review queue:", {
            errorCode: "CK-7825",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "ReviewQueue", id: "LIST" }],
    }),

    getMeasurementsDue: builder.query({
      async queryFn({ clinicId, therapistId, isAdmin }) {
        try {
          const patients = await getMeasurementsDue(clinicId, therapistId, {
            isAdmin,
          });
          return { data: patients };
        } catch (error) {
          console.error("[CK-7826] Error fetching clients with measurements due:", {
            errorCode: "CK-7826",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "MeasurementsDue", id: "LIST" }],
    }),

    // One SMS per cycle — the server refuses a second one
    sendMeasurementReminder: builder.mutation({
      async queryFn({ clinicId, patientId }) {
        try {
          const result = await sendMeasurementReminder(clinicId, patientId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7827] Error sending a measurement reminder:", {
            errorCode: "CK-7827",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "MeasurementsDue", id: "LIST" }],
    }),
  }),
});

export const {
  useGetDailyReportsQuery,
  useAddCoachFoodEntryMutation,
  useUpdateFoodEntryMutation,
  useMarkReportReviewedMutation,
  useAddCoachNoteMutation,
  useDeleteCoachNoteMutation,
  useGetReportsAwaitingReviewQuery,
  useGetMeasurementsDueQuery,
  useSendMeasurementReminderMutation,
} = dailyReportsApi;
